/**
 * The WhatsApp client surface — slice 7's half of `integrations.ts` and
 * `templates.ts`, with slice 9's header media added.
 *
 *   fetchWhatsAppIntegration  GET    /integrations                  the row, or null
 *   connectWhatsApp           PUT    /integrations/whatsapp         connect or replace, then check
 *   checkWhatsApp             POST   /integrations/whatsapp/check   the Test button
 *   disconnectWhatsApp        DELETE /integrations/whatsapp         deletes the row and the token
 *   submitWaTemplate          POST   /templates/:id/submit          draft → pending, at Meta
 *   resubmitWaTemplate        POST   /templates/:id/resubmit        rejected → pending, by a person
 *   fetchWaHeaderMedia        GET    /templates/:id/whatsapp/header-media   the stored file, or null
 *   uploadWaHeaderMedia       POST   /templates/:id/whatsapp/header-media   store the bytes, mint a handle
 *
 * **THERE IS NO DELETE HERE, AND THAT IS DELIBERATE.** A header file comes off a
 * template through slice 4's `DELETE /templates/:id/assets/:assetId`, which is
 * kind-agnostic and already tenant-scoped — a `wa_header_media` row is a
 * `template_assets` row, and a second removal route would be a second place for
 * the storage object and the row to fall out of step. `deleteTemplateAsset` in
 * `./templates.ts` is what the composer calls, for the same reason
 * `AttachmentsPanel` does: neither kind is referenced by a block, so the
 * save-time sweep will never reach either one and an explicit removal is the
 * only way off.
 *
 * **THE SHAPES THAT BELONGED IN `@wend/shared` ARE NOW IN IT.** This file used to
 * carry a hand-written copy of the Meta check vocabulary between two rules,
 * because `contracts/integration.ts` described only SES — `integrationSchema`
 * was `.strict()` with a required `region` and `accessKeyIdLast4`, neither of
 * which a WhatsApp row has. That is fixed: the read shape is a union
 * discriminated on `kind`, the Meta arm and every shape under it live in
 * `@wend/shared`, and the whole block is deleted in favour of the imports below.
 * The API parses the same schemas on the way out that this file parses on the
 * way in, so a drift between the two ends is now a type error rather than a
 * screen that quietly renders `undefined`.
 *
 * **SECURITY.** The system user token travels in exactly one place: the JSON
 * body of the PUT. Not a path segment, not a query string, not a header of our
 * own, and not a retry that rebuilds the request from a URL. It is never logged
 * and there is no field on any response shape it could travel back in — the read
 * shape carries the token's *expiry*, which is the thing the screen needs, and
 * nothing else about it. Because that shape is `.strict()`, an API that started
 * returning a token would fail parsing *here* as well as there.
 */
import {
  connectWhatsAppRequestSchema,
  metaCheckOutcomeSchema,
  metaReadinessSchema,
  metaWhatsAppIntegrationSchema,
  okResponseSchema,
  uploadWaHeaderMediaResponseSchema,
  waHeaderMediaResponseSchema,
  waResubmitTemplateRequestSchema,
  waSubmitTemplateResponseSchema,
  type ConnectWhatsAppRequest,
  type OkResponse,
  type WaHeaderMediaAsset,
  type WaMediaFormat,
  type WaResubmitTemplateRequest,
  type WaSubmitTemplateResponse,
} from '@wend/shared';
import { z } from 'zod';

import { apiRequest } from './client';
import { fetchIntegrationRows } from './integrations';

/*
 * Re-exported so the screens and `lib/whatsapp-display.ts` keep importing the
 * WhatsApp vocabulary from one place. They are `@wend/shared`'s declarations —
 * nothing about a Meta check is described in this app any more.
 */
export {
  connectWhatsAppRequestSchema,
  metaCheckFailureSchema,
  metaCheckOutcomeSchema,
  metaCheckResultSchema,
  metaPhoneNumberInfoSchema,
  metaReadinessSchema,
  metaReadinessStateSchema,
  metaTokenExpiry,
  metaTokenExpiryStateSchema,
  metaTokenInfoSchema,
  metaTransportSchema,
  metaWabaInfoSchema,
  metaWhatsAppIntegrationSchema,
  META_FAILURE_CODE_VALUES,
  META_READINESS_STATE_VALUES,
  META_TOKEN_EXPIRY_VALUES,
  META_TOKEN_EXPIRY_WARNING_MS,
  META_TRANSPORT_VALUES,
  type ConnectWhatsAppRequest,
  type MetaCheckFailure,
  type MetaCheckOutcome,
  type MetaCheckResult,
  type MetaFailureCode,
  type MetaPhoneNumberInfo,
  type MetaReadiness,
  type MetaReadinessState,
  type MetaTokenExpiryState,
  type MetaTokenInfo,
  type MetaTransport,
  type MetaWabaInfo,
} from '@wend/shared';

/**
 * The stored WhatsApp integration, redacted — `@wend/shared`'s
 * `metaWhatsAppIntegrationSchema` under this app's older name.
 *
 * There is no token on it and there never can be: the field does not exist on
 * the shape, and the shape is `.strict()`. What the row does carry is
 * `tokenExpiresAt`, because a token that quietly expires is this slice's largest
 * operational risk and this is the screen where it stops being quiet.
 */
export const whatsAppIntegrationSchema = metaWhatsAppIntegrationSchema;
export type WhatsAppIntegration = z.infer<typeof whatsAppIntegrationSchema>;

/**
 * What a connect and a test answer with.
 *
 * Three parts, and each is needed. `integration` is the persisted row the card
 * renders. `outcome` carries what the row cannot — **which transport answered**,
 * so a green from a stub can never be mistaken for a green from Meta, and the
 * failure *code*, which heads the message with what went wrong. `readiness` is
 * the API's own verdict, computed there by `metaReadiness()` and not re-derived
 * here: the server decides whether a campaign may be scheduled, and a browser
 * that worked the verdict out for itself is a browser that can disagree with it.
 */
export const whatsAppCheckResponseSchema = z
  .object({
    integration: metaWhatsAppIntegrationSchema,
    outcome: metaCheckOutcomeSchema,
    readiness: metaReadinessSchema.nullable().default(null),
  })
  .strict();
export type WhatsAppCheckResponse = z.infer<typeof whatsAppCheckResponseSchema>;

/* -------------------------------------------------------------------------- */
/* The integration                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The WhatsApp integration, or `null` when this workspace has not connected one.
 *
 * Read off the list route for the same reason `fetchSesIntegration` is:
 * `GET /integrations` answers `[]` for a workspace that has connected nothing,
 * while `GET /integrations/whatsapp` answers 404 — and "not connected yet" is
 * the ordinary starting state of this card, not an error to catch and discard.
 */
export async function fetchWhatsAppIntegration(
  signal?: AbortSignal,
): Promise<WhatsAppIntegration | null> {
  const rows = await fetchIntegrationRows(signal);
  const row = rows.find((candidate) => candidate.kind === 'meta_whatsapp');
  return row === undefined ? null : whatsAppIntegrationSchema.parse(row);
}

/**
 * Connects the tenant's own Meta WhatsApp Business account, or replaces the one
 * already connected, and checks it in the same round trip.
 *
 * `PUT`, because it is idempotent and replaces wholesale — `UNIQUE (tenant_id,
 * kind)` — and because there is no partial update: rotating the token means
 * sending all three fields, since a half-updated credential fails at send time
 * rather than at the form.
 *
 * A check that fails is still a 200. The credentials were stored, the row says
 * `failed`, and `outcome.failure.message` says what to repair — so this rejects
 * only when the *request* failed, never when Meta did.
 */
export async function connectWhatsApp(
  request: ConnectWhatsAppRequest,
): Promise<WhatsAppCheckResponse> {
  return apiRequest({
    path: '/integrations/whatsapp',
    method: 'PUT',
    body: connectWhatsAppRequestSchema.parse(request),
    schema: whatsAppCheckResponseSchema,
  });
}

/** Re-runs the check against the stored token. The Test button. */
export async function checkWhatsApp(): Promise<WhatsAppCheckResponse> {
  return apiRequest({
    path: '/integrations/whatsapp/check',
    method: 'POST',
    schema: whatsAppCheckResponseSchema,
  });
}

/** Disconnects: the row goes, and the sealed token goes with it. */
export async function disconnectWhatsApp(): Promise<OkResponse> {
  return apiRequest({
    path: '/integrations/whatsapp',
    method: 'DELETE',
    // `okResponseSchema`, not a hand-written `{ ok: true }`. Every OK envelope
    // in this API is `{ success: true }` — `disconnectSes` parses it with this
    // same schema — and a locally invented shape would have made a successful
    // disconnect look to the card like a malformed response.
    schema: okResponseSchema,
  });
}

/* -------------------------------------------------------------------------- */
/* Templates                                                                   */
/* -------------------------------------------------------------------------- */

function templatePath(templateId: string, suffix: string): string {
  return `/templates/${encodeURIComponent(templateId)}${suffix}`;
}

/**
 * Sends a WhatsApp draft to Meta for review.
 *
 * No body: what is submitted is what is stored, so the template a person read on
 * screen is the template Meta receives. Anything unsaved must be saved first —
 * the composer does that in the same action rather than submitting a document
 * nobody has seen.
 */
export async function submitWaTemplate(templateId: string): Promise<WaSubmitTemplateResponse> {
  return apiRequest({
    path: templatePath(templateId, '/submit'),
    method: 'POST',
    schema: waSubmitTemplateResponseSchema,
  });
}

/**
 * Sends a **rejected** template back to Meta, after a person fixed it.
 *
 * THE HUMAN ACTION. Nothing in Wend resubmits on its own — a rejection loop
 * against Meta is how a WABA gets flagged — so this request cannot be made
 * without `acknowledgedReason`, which is a person restating what Meta refused,
 * and the corrected document, which travels with it.
 */
export async function resubmitWaTemplate(
  templateId: string,
  request: WaResubmitTemplateRequest,
): Promise<WaSubmitTemplateResponse> {
  return apiRequest({
    path: templatePath(templateId, '/resubmit'),
    method: 'POST',
    body: waResubmitTemplateRequestSchema.parse(request),
    schema: waSubmitTemplateResponseSchema,
  });
}

/* -------------------------------------------------------------------------- */
/* Header media                                                                */
/* -------------------------------------------------------------------------- */

function headerMediaPath(templateId: string): string {
  return templatePath(templateId, '/whatsapp/header-media');
}

/**
 * The file behind this template's media header, or `null` when it has none.
 *
 * **`null` is an answer, not a failure.** Every text header and every
 * header-less template is in that state, and so is a template a person is part
 * way through composing. `waHeaderMediaResponseSchema` is `.strict()` and its
 * `asset` is nullable for exactly this reason, so an API that started answering
 * `{}` — or an empty object where the file belongs — fails parsing here as well
 * as there rather than rendering as an empty file card.
 *
 * This is a separate request from `fetchTemplate`, and not a field on
 * `Template.assets`, because the asset shape a template carries is slice 4's
 * `templateAsset` — it has a `kind` and a `url` and knows nothing about a Meta
 * handle. The handle is the whole point of the read: without it the composer
 * cannot tell "a file with a handle minted from it" from "a file waiting for
 * one", which are the two states that decide whether the header can be saved.
 */
export async function fetchWaHeaderMedia(
  templateId: string,
  signal?: AbortSignal,
): Promise<WaHeaderMediaAsset | null> {
  const response = await apiRequest({
    path: headerMediaPath(templateId),
    schema: waHeaderMediaResponseSchema,
    signal,
  });
  return response.asset;
}

/**
 * Stores the bytes against the template and mints the handle Meta reviews.
 *
 * Multipart under the field name `file`, exactly as `uploadTemplateAsset` is:
 * `apiRequest` sends a `FormData` verbatim and leaves the `Content-Type` to the
 * browser so the multipart boundary matches the body.
 *
 * **`format` is a query parameter and not a form field**, because it chooses the
 * byte ceiling and the server has to know which ceiling to buffer under before
 * it parses the body — a limit that can only be applied after the file is in
 * memory is not a limit. `uploadWaHeaderMediaQuerySchema` carries `format` and
 * nothing else; there is deliberately no `kind` on this route, so a caller
 * cannot ask for `inline_image` here and get slice 4's MIME list applied to a
 * file bound for Meta.
 *
 * **Nothing about the file is asserted here.** The composer refuses the obvious
 * cases first — see `WaHeaderMediaPanel` — but the decision that counts is the
 * server's, made by sniffing the bytes rather than trusting the `Content-Type`
 * the browser guessed from a file extension.
 *
 * The answer is the asset, whose `reviewHandle` may be `null`: the bytes are
 * stored either way, and that is the durable half of the job. Treating a
 * missing handle as a failed upload would invite somebody to upload the same
 * file again.
 */
export async function uploadWaHeaderMedia(
  templateId: string,
  file: File,
  format: WaMediaFormat,
): Promise<WaHeaderMediaAsset> {
  const body = new FormData();
  body.append('file', file);

  const response = await apiRequest({
    path: headerMediaPath(templateId),
    method: 'POST',
    query: { format },
    body,
    schema: uploadWaHeaderMediaResponseSchema,
  });
  return response.asset;
}
